// Global Instructions Rule Applied!
//
// Configuration → Interaction Tracking admin models.
//
// Mirrors public.view__interaction_tracking_admin. cfg.tracked_mailboxes is the
// source of truth for which mailboxes Reservoir watches and on which channel —
// BOTH Graph subscription managers read it, where each used to carry its own
// hardcoded array (and the two disagreed: 15 mail, 12 calendar).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const TrackedMailboxRowSchema = z
  .object({
    tracked_mailboxes__id: z.string(),
    tracked_mailboxes__mailbox: z.string(),
    tracked_mailboxes__display_name: z.string().nullish(),
    /** Watch this mailbox's mail. Switching it off deletes the Graph
     *  subscription immediately, so mail from then on is never captured. */
    tracked_mailboxes__email_tracking_enabled: z.boolean().nullish(),
    /** Watch this mailbox's calendar. Independent of mail — a separate Graph
     *  subscription on a separate app registration. */
    tracked_mailboxes__event_tracking_enabled: z.boolean().nullish(),
    /** Does this mailbox take part in email categorisation. One email is one
     *  inbound_events row however many mailboxes received it, so it is
     *  classified when ANY recipient wants it and stamped only where on.
     *  Mail only — calendar events are not categorised. */
    tracked_mailboxes__categorization_enabled: z.boolean().nullish(),
    /** Send to Outlook (agent drafts, Phase 6a) may create drafts in this
     *  mailbox for its owner. AND-ed with the global cfg switch; needs mail
     *  tracking because the sent-mail detection rides the subscription. */
    tracked_mailboxes__outlook_drafts_enabled: z.boolean().nullish(),
    /** Email campaigns on the mailbox channel may be sent from this mailbox
     *  (capital call notices, stage 6a). AND-ed with the environment's
     *  cfg.app_config email/mailbox_send switch. Independent of mail
     *  tracking - the send goes through Graph, not the subscription. */
    tracked_mailboxes__campaign_send_enabled: z.boolean().nullish(),
    /** reservoir@ — mail here creates records. Its mail cannot be untracked
     *  from the UI; its calendar can. */
    tracked_mailboxes__is_command_mailbox: z.boolean().nullish(),
    tracked_mailboxes__email_tracking_last_enabled_at: z.string().nullish(),
    tracked_mailboxes__event_tracking_last_enabled_at: z.string().nullish(),
    tracked_mailboxes__notes: z.string().nullish(),
    /** Reality rather than intent, per channel. */
    prog__email_subscription_live: z.boolean().nullish(),
    prog__event_subscription_live: z.boolean().nullish(),
    prog__label_override_count: z.number().nullish(),
    /** Set only on a save response when the immediate subscription teardown
     *  could not be reached — the cfg change stands, the subscription lapses
     *  on its own schedule instead. */
    prog__teardown_warning: z.string().nullish(),
    /** Sort key the RPC emits alongside the aliased columns. */
    mailbox: z.string().nullish()
  })
  .strict()

export type TrackedMailboxRow = z.infer<typeof TrackedMailboxRowSchema>

export const MailboxCategoryLabelSchema = z
  .object({
    tracked_mailbox_category_labels__id: z.string(),
    tracked_mailbox_category_labels__tracked_mailbox_id: z.string(),
    tracked_mailbox_category_labels__email_category_id: z.string(),
    tracked_mailbox_category_labels__outlook_label: z.string().nullish(),
    tracked_mailbox_category_labels__outlook_color: z.string().nullish()
  })
  .strict()

export type MailboxCategoryLabel = z.infer<typeof MailboxCategoryLabelSchema>

export interface InteractionTrackingConfigAdminData {
  mailboxes: TrackedMailboxRow[]
  labelOverrides: MailboxCategoryLabel[]
}

export function validateTrackedMailboxRow(
  data: unknown,
  options?: { isArray?: boolean }
): TrackedMailboxRow | TrackedMailboxRow[] {
  try {
    return validateWithZodSchema(TrackedMailboxRowSchema, data, 'tracked mailbox', options) as
      TrackedMailboxRow | TrackedMailboxRow[]
  } catch (e) {
    throw new Error(`Tracked mailbox validation failed: ${e}`)
  }
}

export function validateMailboxCategoryLabel(
  data: unknown,
  options?: { isArray?: boolean }
): MailboxCategoryLabel | MailboxCategoryLabel[] {
  try {
    return validateWithZodSchema(MailboxCategoryLabelSchema, data, 'mailbox label override', options) as
      MailboxCategoryLabel | MailboxCategoryLabel[]
  } catch (e) {
    throw new Error(`Mailbox label override validation failed: ${e}`)
  }
}
